import React, { useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FormErrorProps {
  message?: string;
  fieldName?: string;
  scrollToView?: boolean;
  autoFocus?: boolean;
  className?: string;
}

export const FormError: React.FC<FormErrorProps> = ({
  message,
  fieldName,
  scrollToView = false,
  autoFocus = false,
  className
}) => {
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message && errorRef.current) {
      if (scrollToView) {
        errorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }

      if (autoFocus) {
        errorRef.current.focus();
      }
    }
  }, [message, scrollToView, autoFocus]);

  if (!message) {
    return null;
  }

  return (
    <div
      ref={errorRef}
      role="alert"
      aria-live="polite"
      tabIndex={autoFocus ? 0 : -1}
      className={cn(
        'flex items-center gap-2 text-sm text-red-600 mt-1',
        className
      )}
      id={fieldName ? `${fieldName}-error` : undefined}
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
};

interface FormFieldErrorProps {
  error?: { message?: string };
  fieldName?: string;
  scrollToView?: boolean;
  autoFocus?: boolean;
  className?: string;
}

export const FormFieldError: React.FC<FormFieldErrorProps> = ({
  error,
  fieldName,
  scrollToView = false,
  autoFocus = false,
  className
}) => {
  return (
    <FormError
      message={error?.message}
      fieldName={fieldName}
      scrollToView={scrollToView}
      autoFocus={autoFocus}
      className={className}
    />
  );
};

export default FormError;
